import type { CardSearchParams } from './types';

/** Backend route builders (paths only — prefix with the API base URL). */
export const API_ROUTES = {
  // Auth
  authRegister: '/auth/register',
  authLogin: '/auth/login',
  authRefresh: '/auth/refresh',
  authLogout: '/auth/logout',

  // Cards (public — no auth required)
  cards: '/cards',
  card: (name: string) => `/cards/${encodeURIComponent(name)}`,
  cardFilters: '/cards/filters',

  // Decks (create/list/update/delete require auth; GET by id + public list are public)
  decks: '/decks',
  decksPublic: '/decks/public',
  deck: (id: string) => `/decks/${encodeURIComponent(id)}`,

  // Friends (all require auth)
  friends: '/friends',
  friendRequests: '/friends/requests',
  friendRequest: '/friends/request',
  friendAccept: '/friends/accept',
  friendDecline: '/friends/decline',
  friendsDecks: '/friends/decks',
  friend: (userId: string) => `/friends/${encodeURIComponent(userId)}`,

  // Public profiles (optional auth)
  user: (userId: string) => `/users/${encodeURIComponent(userId)}`,
  userDecks: (userId: string) => `/users/${encodeURIComponent(userId)}/decks`,
  usersSearch: (username: string) => `/users?username=${encodeURIComponent(username)}`,

  // Duel rooms (require auth). Real-time play runs over the WebSocket API.
  duels: '/duel',
  duel: (id: string) => `/duel/${encodeURIComponent(id)}`,
} as const;

/** Build the query string for GET /cards from a set of search params.
 * Multi-value filters are serialized as comma-separated lists. */
export function buildCardsQuery(params: CardSearchParams): string {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  const csv = (key: string, vals?: string[]) => {
    if (vals && vals.length) q.set(key, vals.join(','));
  };
  csv('categories', params.categories);
  csv('attributes', params.attributes);
  csv('monsterTypes', params.monsterTypes);
  csv('monsterCategories', params.monsterCategories);
  csv('spellTypes', params.spellTypes);
  csv('trapTypes', params.trapTypes);
  csv('skillCharacters', params.skillCharacters);
  csv('sets', params.sets);
  if (params.page != null) q.set('page', String(params.page));
  if (params.pageSize != null) q.set('pageSize', String(params.pageSize));
  if (params.sort) q.set('sort', params.sort);
  const s = q.toString();
  return s ? `?${s}` : '';
}
